import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import type { market as marketEn } from '@/i18n/locales/en/market';
import { cn } from '@/lib/utils';
import type {
  AuctionStatus,
  BackorderHold,
  BackorderStatus,
  CaseKind,
  DepositFormatStatus,
  DepositStatus,
  DropCatchStatus,
  DropStatus,
  EscrowVerificationStatus,
  LockType,
  PublicationDisposition,
} from '../../api/areas/market';
import { Badge } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { formatDateTime, formatMoney } from '../../lib/format';
import { humanize, type Meta } from '../domains/status';

/**
 * Amounts arrive as exact decimal strings and are formatted as strings with their currency:
 * no digit is invented, dropped or rounded on the way to the screen.
 */
export function money(amount: string | null | undefined, currency: string): string {
  if (amount === null || amount === undefined) return '—';
  return formatMoney({ amount, currency });
}

/** Exact comparison of two non-negative decimal strings: `a >= b`. */
export function decimalGte(a: string, b: string): boolean {
  const [aw = '', af = ''] = a.trim().split('.');
  const [bw = '', bf = ''] = b.trim().split('.');
  const width = Math.max(af.length, bf.length);
  return BigInt(aw + af.padEnd(width, '0')) >= BigInt(bw + bf.padEnd(width, '0'));
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * `over` marks the statuses an auction never leaves. The top bid of one of those is the highest
 * bid received, not a bidder still "leading" — `Record<AuctionStatus, …>` makes TypeScript
 * demand a verdict for every status, so a new one cannot quietly default to "still running".
 */
export const AUCTION_STATUS: Record<AuctionStatus, Meta & { over?: true }> = {
  SCHEDULED: { label: 'Scheduled', tone: 'pending' },
  OPEN: { label: 'Open', tone: 'info' },
  CLOSED: { label: 'Closed', tone: 'warn' },
  SETTLING: { label: 'Settling', tone: 'pending' },
  SETTLED: { label: 'Settled', tone: 'ok', over: true },
  UNSOLD: { label: 'Unsold', tone: 'neutral', over: true },
  FAILED: { label: 'Failed', tone: 'crit', over: true },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', over: true },
};

/** True once the auction can no longer change hands: nobody is "leading" any more. */
export const isOver = (status: AuctionStatus): boolean => AUCTION_STATUS[status].over === true;

export const BACKORDER_STATUS: Record<BackorderStatus, Meta> = {
  ACTIVE: { label: 'Active', tone: 'info' },
  ASSIGNED: { label: 'Assigned', tone: 'pending' },
  IN_AUCTION: { label: 'In auction', tone: 'pending' },
  WON: { label: 'Won', tone: 'ok' },
  LOST: { label: 'Lost', tone: 'neutral' },
  REGISTERING: { label: 'Registering', tone: 'pending' },
  CAUGHT: { label: 'Caught', tone: 'ok' },
  NOT_CAUGHT: { label: 'Not caught', tone: 'serious' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const DROP_STATUS: Record<DropStatus, Meta> = {
  SCHEDULED: { label: 'Scheduled', tone: 'pending' },
  AUCTION: { label: 'In auction', tone: 'info' },
  ASSIGNED: { label: 'Assigned', tone: 'pending' },
  CAUGHT: { label: 'Caught', tone: 'ok' },
  NOT_CAUGHT: { label: 'Not caught', tone: 'serious' },
  NO_DEMAND: { label: 'No demand', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const CATCH_STATUS: Record<DropCatchStatus, Meta> = {
  PLANNED: { label: 'Planned', tone: 'pending' },
  POLLING: { label: 'Polling', tone: 'info' },
  REGISTERING: { label: 'Registering', tone: 'pending' },
  CAUGHT: { label: 'Caught', tone: 'ok' },
  NOT_CAUGHT: { label: 'Not caught', tone: 'serious' },
  MANUAL_REVIEW: { label: 'Manual review', tone: 'crit' },
};

export const HOLD_STATUS: Record<BackorderHold['status'], Meta> = {
  ACTIVE: { label: 'Held', tone: 'info' },
  RELEASED: { label: 'Released', tone: 'neutral' },
  CAPTURED: { label: 'Charged', tone: 'ok' },
  EXPIRED: { label: 'Expired', tone: 'warn' },
};

/** A customer, by display name, linking to their account. Never renders a bare identifier. */
export function CustomerRef({ id, name, sub }: { id: string | null | undefined; name: string | null | undefined; sub?: ReactNode }) {
  return <EntityRef label={name} id={id} to={id ? `/customers/${encodeURIComponent(id)}` : undefined} sub={sub} />;
}

export const DEPOSIT_STATUS: Record<DepositStatus, Meta> = {
  PENDING: { label: 'Pending', tone: 'pending' },
  GENERATED: { label: 'Generated', tone: 'pending' },
  VALIDATED: { label: 'Validated', tone: 'pending' },
  VALIDATION_FAILED: { label: 'Validation failed', tone: 'crit' },
  ENCRYPTED: { label: 'Encrypted', tone: 'pending' },
  DELIVERING: { label: 'Delivering', tone: 'pending' },
  DELIVERED: { label: 'Delivered', tone: 'info' },
  DELIVERY_FAILED: { label: 'Delivery failed', tone: 'serious' },
  DELIVERY_UNKNOWN: { label: 'Delivery unknown', tone: 'serious' },
  CONFIRMED: { label: 'Confirmed', tone: 'ok' },
  VERIFICATION_FAILED: { label: 'Verification failed', tone: 'crit' },
  ABANDONED: { label: 'Abandoned', tone: 'neutral' },
};

export const VERIFICATION_STATUS: Record<EscrowVerificationStatus, Meta> = {
  RECEIVED: { label: 'Received', tone: 'info' },
  VERIFIED: { label: 'Verified', tone: 'ok' },
  VERIFICATION_FAILED: { label: 'Verification failed', tone: 'crit' },
};

export const LOCK_TYPE: Record<LockType, Meta> = {
  REGISTRAR_LOCK: { label: 'Registrar lock', tone: 'neutral' },
  TRANSFER_LOCK: { label: 'Transfer lock', tone: 'neutral' },
  UDRP_LOCK: { label: 'UDRP lock', tone: 'warn' },
  COURT_LOCK: { label: 'Court lock', tone: 'crit' },
  REGISTRY_LOCK: { label: 'Registry lock', tone: 'serious' },
  COMPLIANCE_LOCK: { label: 'Compliance lock', tone: 'serious' },
};

export const CASE_KIND: Record<CaseKind, Meta & { base: string }> = {
  ABUSE: { label: 'Abuse', tone: 'serious', base: '/compliance/icann/abuse' },
  DISPUTE: { label: 'Dispute', tone: 'warn', base: '/compliance/icann/disputes' },
  DISCLOSURE: { label: 'Disclosure', tone: 'info', base: '/compliance/icann/disclosure' },
};

export const DISPOSITION: Record<PublicationDisposition, Meta> = {
  PUBLISH: { label: 'Publish', tone: 'ok' },
  PUBLISH_IF_CONSENTED: { label: 'Publish if consented', tone: 'info' },
  REDACT: { label: 'Redact', tone: 'warn' },
  CONTACT_FORM: { label: 'Contact form', tone: 'info' },
  OMIT: { label: 'Omit', tone: 'neutral' },
};

/** Flags a deposit made in the internal draft format (ARCHITECTURE §14): never an official RDE deposit. */
export function FormatBadge({ status, format }: { status: DepositFormatStatus; format?: string }) {
  const { t } = useTranslation('market');
  return status === 'OFFICIAL' ? (
    <Badge tone="ok">{format ?? t('format.official')}</Badge>
  ) : (
    <Badge tone="warn" title={t('format.draftTitle')}>
      {t('format.draft')}
      {format ? ` · ${format}` : ''}
    </Badge>
  );
}

export function DraftFormatNotice({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('market');
  return (
    <Callout tone="warn" title={t('format.noticeTitle')}>
      {t('format.notice', { format: 'UNOFFICIAL_DRAFT' })} {children}
    </Callout>
  );
}

export type MarketEnumGroup = keyof (typeof marketEn)['enum'];

/** Translated label for a market enum value; unknown values fall back to the humanised code. */
export function marketEnum(group: MarketEnumGroup, value: string): string {
  const key = `market:enum.${group}.${value}`;
  return i18n.exists(key) ? (i18n.t as unknown as (k: string) => string)(key) : humanize(value);
}

/** Status badge with a translated label and the raw code in `title`. */
export function MarketBadge({ group, map, value }: { group: MarketEnumGroup; map: Readonly<Record<string, Meta>>; value: string | null | undefined }) {
  useTranslation('market');
  if (!value) return <Badge tone="neutral">—</Badge>;
  const meta = map[value];
  return (
    <Badge tone={meta?.tone ?? 'neutral'} title={value}>
      {marketEnum(group, value)}
    </Badge>
  );
}

/**
 * The answer to the last availability check. The worker writes `AVAILABLE`, `UNAVAILABLE`, or
 * `ERROR …` with the registry's own wording, so only the two coded answers are translated and
 * anything else is shown exactly as the registry said it.
 */
export function checkResult(value: string | null | undefined): string | null {
  if (!value) return null;
  return value === 'AVAILABLE' || value === 'UNAVAILABLE' ? marketEnum('checkResult', value) : value;
}

/** Live auctions ending within this window are flagged "closing soon". */
export const CLOSING_SOON_MS = 6 * 3_600_000;

/** Current time, re-rendering every `intervalMs`. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A ticking "ends in" / "starts in" countdown to `to`; `hh:mm:ss`, with a day prefix when needed. */
export function Countdown({ to, now, className, prefix = true, mode = 'ends' }: { to: string; now: number; className?: string; prefix?: boolean; mode?: 'ends' | 'starts' }) {
  const { t } = useTranslation('market');
  const left = new Date(to).getTime() - now;
  if (Number.isNaN(left)) return null;
  if (left <= 0) {
    return (
      <span className={cn('text-muted-foreground', className)} title={formatDateTime(to)}>
        {t('countdown.ended')}
      </span>
    );
  }
  const s = Math.floor(left / 1000);
  const d = Math.floor(s / 86_400);
  const clock = `${pad(Math.floor((s % 86_400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return (
    <span className={cn('tabular whitespace-nowrap', className)} title={formatDateTime(to)}>
      {prefix ? <span className="font-normal text-muted-foreground">{mode === 'ends' ? t('countdown.endsIn') : t('countdown.startsIn')} </span> : null}
      <time dateTime={to}>
        {d > 0 ? `${t('countdown.days', { count: d })} ` : ''}
        {clock}
      </time>
    </span>
  );
}

/** A pulsing dot for live auctions. */
export function LiveDot({ className }: { className?: string }) {
  return (
    <span className={cn('relative inline-flex size-2', className)} aria-hidden="true">
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
      <span className="relative inline-flex size-2 rounded-full bg-success" />
    </span>
  );
}
